import { useNavigate } from 'react-router-dom';
import { useUpgradeStore, useAuthStore } from '@dukene/shared';
import { 
  X, 
  ChevronRight, 
  Check, 
  Sparkles, 
  Zap, 
  Crown,
  ArrowUpCircle
} from 'lucide-react';

export default function UpgradeModal() {
  const navigate = useNavigate();
  const { isOpen, trigger, close } = useUpgradeStore();
  const { user } = useAuthStore();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    close();
    navigate('/settings/billing');
  };

  const isChannelTrigger = trigger === 'sales_channel_limit';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity animate-in fade-in duration-300" 
        onClick={close}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-[#F6F6F7] rounded-[32px] overflow-hidden shadow-2xl border border-white/20 animate-in zoom-in-95 duration-300">
        
        {/* Header Section */}
        <div className="bg-black p-8 text-white relative">
          <button 
            onClick={close}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/20 text-emerald text-[10px] font-black uppercase tracking-widest mb-6">
            <Sparkles className="w-3 h-3 text-emerald shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
            Upgrade Recommended
          </div>

          <h2 className="text-4xl font-black tracking-tight leading-none mb-3">
            {isChannelTrigger ? 'Unlock Sales Channels' : trigger === 'store_limit' ? 'Grow Your Empire' : 'Unlock More Power'}
          </h2>
          <p className="text-white/60 font-medium text-lg max-w-md">
            {isChannelTrigger
              ? `The ${user?.plan || 'BASIC'} plan allows 1 sales channel (Online Store). Upgrade to connect WhatsApp, POS, and more.`
              : trigger === 'store_limit' 
              ? `You've reached the store limit for the ${user?.plan || 'BASIC'} plan. Scale up with multi-store management.`
              : 'Unlock advanced multi-channel selling and lower transaction fees.'}
          </p>

          <div className="absolute -bottom-6 right-8 w-16 h-16 bg-emerald rounded-2xl flex items-center justify-center shadow-xl shadow-emerald/20">
            <ArrowUpCircle className="w-10 h-10 text-white" />
          </div>
        </div>

        {/* Plan Comparison */}
        <div className="p-8 pb-12">
          <div className="grid grid-cols-2 gap-6 mt-4">
            
            {/* PRO Plan */}
            <div className="bg-white p-6 rounded-[28px] border-2 border-transparent hover:border-emerald/30 transition-all group shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald/10 rounded-2xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-emerald" />
                  </div>
                  <div className="px-2.5 py-0.5 bg-emerald/10 text-emerald text-[10px] font-bold rounded-full uppercase">3% Tx Fee</div>
                </div>
                <h3 className="text-2xl font-black text-black mb-1">PRO</h3>
                <p className="text-sm font-bold text-gray-400 mb-4 uppercase tracking-wider">KES 3,500 / month</p>
                
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald" />
                    </div>
                    Up to 2 Stores
                  </li>
                  <li className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald" />
                    </div>
                    3 Sales Channels / Store
                  </li>
                  <li className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald" />
                    </div>
                    3% Platform Fee (Save 50%)
                  </li>
                  <li className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald" />
                    </div>
                    WhatsApp CRM & Notifications
                  </li>
                </ul>
              </div>
            </div>

            {/* MAX Plan */}
            <div className="bg-black p-6 rounded-[28px] border-2 border-emerald/50 transition-all shadow-xl relative overflow-hidden group flex flex-col justify-between">
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald rounded-2xl flex items-center justify-center">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div className="px-3 py-1 bg-emerald text-black text-[10px] font-black rounded-full uppercase">2% Tx Fee</div>
                </div>
                <h3 className="text-2xl font-black text-white mb-1">MAX</h3>
                <p className="text-sm font-bold text-white/40 mb-4 uppercase tracking-wider">KES 9,500 / month</p>
                
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm font-semibold text-white/90">
                    <div className="w-5 h-5 rounded-full bg-emerald/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald" />
                    </div>
                    Up to 5 Stores
                  </li>
                  <li className="flex items-center gap-3 text-sm font-semibold text-white/90">
                    <div className="w-5 h-5 rounded-full bg-emerald/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald" />
                    </div>
                    All Sales Channels Unlocked
                  </li>
                  <li className="flex items-center gap-3 text-sm font-semibold text-white/90">
                    <div className="w-5 h-5 rounded-full bg-emerald/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald" />
                    </div>
                    2% Lowest Platform Fee
                  </li>
                  <li className="flex items-center gap-3 text-sm font-semibold text-white/90">
                    <div className="w-5 h-5 rounded-full bg-emerald/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald" />
                    </div>
                    Advanced Analytics & API
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center gap-4">
            <button
              onClick={handleUpgrade}
              className="w-full h-16 bg-black text-white rounded-2xl font-black text-lg hover:bg-gray-800 transition-all shadow-xl hover:shadow-emerald/20 flex items-center justify-center gap-3 transform hover:-translate-y-1 active:translate-y-0"
            >
              Choose Your Plan
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={close}
              className="text-sm font-bold text-gray-400 hover:text-black transition-colors"
            >
              Maybe later, keep my current plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
